// File: src/components/friends/MatchesAndRequestsPage.jsx

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProfileActionSheet from '../ProfileActionSheet';  // instead of ProfileModal
import { LuUserPlus, LuHandshake} from "react-icons/lu";
import { IoChevronForward, IoPeopleOutline, IoHeartOutline } from 'react-icons/io5';
import { useToaster } from '../../context/ToasterContext'; // <--- 1) import useToaster
import MinimalFooter from "../MinimalFooter";
import axios from 'axios';
function IconFA({ name, style = {} }) {
  return <i className={`fas fa-${name}`} style={style} aria-hidden="true" />;
}

export default function MatchesAndRequestsPage({
  needDecide,
  potential,
  accepted,
  currentUserId,
  onAccept,
  onDecline,
  onUndo,
  onCancel,
  onSendRequest,
  // For blocking/report feature
  onUserBlocked,
  onMessage,
}) {
  console.log('[MatchesAndRequestsPage] Props:', { needDecide, potential, accepted, currentUserId });

  // 2) Destructure the toaster
  const { addToast } = useToaster();

  const [hostEmails, setHostEmails] = useState(new Set());
const [hostUserIds, setHostUserIds] = useState(new Set());

useEffect(() => {
  axios
    .get(`${process.env.REACT_APP_API_URL}/promoters`)
    .then((res) => {
      const promoters = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.promoters)
          ? res.data.promoters
          : [];

      const emails = new Set(
        promoters
          .map((promoter) =>
            String(promoter?.email || '').trim().toLowerCase()
          )
          .filter(Boolean)
      );

      setHostEmails(emails);
    })
    .catch((err) => {
      console.error('[MatchesAndRequestsPage] Failed to load hosts:', err);
    });
}, []);

  // --------------------------------------
  // Filter out unnamed/deleted users for REQUESTS
  // --------------------------------------
  const validNeedDecide = (needDecide || []).filter(({ req }) => {
    if (!req) {
      console.log('[MatchesAndRequestsPage] Skipping entry with no req');
      return false;
    }
    const { fromUserId, toUserId } = req;
    const iAmReceiver = String(toUserId?._id || toUserId) === String(currentUserId);
    const otherUser = iAmReceiver ? fromUserId : toUserId;
    const isValid =
      otherUser &&
      otherUser._id &&
      (otherUser.firstName?.trim() || otherUser.lastName?.trim());
    console.log('[MatchesAndRequestsPage] validNeedDecide entry for req:', req._id, 'isValid:', isValid);
    return isValid;
  });
  console.log('[MatchesAndRequestsPage] Total validNeedDecide:', validNeedDecide.length);

  // --------------------------------------
  // Potential is now already flattened by Friends.js
  // --------------------------------------
  const pairsToShow = potential || [];
  const matchesCount = pairsToShow.length;
  const requestsCount = validNeedDecide.length;

  // Profile modal + accordion state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileModalUser, setProfileModalUser] = useState(null);
  const [profileModalEvent, setProfileModalEvent] = useState('');
  const [openSection, setOpenSection] = useState(null);

  // For auto‑focusing a match request from query params
  const location = useLocation();
  const navigate = useNavigate();
  const [focusedRequestId, setFocusedRequestId] = useState(null);


     // Build Authorization header for protected user profile fetches
     function authHeaders() {
      const token =
        localStorage.getItem("accessToken") ||
        JSON.parse(localStorage.getItem("tribe_auth") || "{}")?.token ||
        "";
      return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    }

    useEffect(() => {
  if (hostEmails.size === 0) return;

  const usersById = new Map();

  // People You Met
  (potential || []).forEach(({ otherUser }) => {
    const id = String(otherUser?._id || '');
    if (id) {
      usersById.set(id, otherUser);
    }
  });

  // Friend Requests
  (needDecide || []).forEach(({ req }) => {
    if (!req) return;

    const { fromUserId, toUserId } = req;

    const iAmReceiver =
      String(toUserId?._id || toUserId) === String(currentUserId);

    const otherUser = iAmReceiver
      ? fromUserId
      : toUserId;

    const id = String(otherUser?._id || '');

    if (id) {
      usersById.set(id, otherUser);
    }
  });

  let cancelled = false;

  async function resolveHosts() {
    const hostIds = [];

    await Promise.all(
      [...usersById.entries()].map(async ([userId, user]) => {
        try {
          let email = String(user?.email || '')
            .trim()
            .toLowerCase();

          // Match/request response didn't include email.
          // Fetch the full profile.
          if (!email) {
            const { data } = await axios.get(
              `${process.env.REACT_APP_API_URL}/users/${encodeURIComponent(userId)}`,
              {
                ...authHeaders(),
              }
            );

            email = String(data?.email || '')
              .trim()
              .toLowerCase();
          }

          if (email && hostEmails.has(email)) {
            hostIds.push(userId);
          }
        } catch (err) {
          console.warn(
            '[MatchesAndRequestsPage] Failed to resolve host status:',
            userId,
            err?.message
          );
        }
      })
    );

    if (!cancelled) {
      setHostUserIds(new Set(hostIds));
    }
  }

  resolveHosts();

  return () => {
    cancelled = true;
  };
}, [hostEmails, potential, needDecide, currentUserId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focusId = params.get('focusRequestId');
    console.log('[MatchesAndRequestsPage] focusRequestId from URL:', focusId);
    setFocusedRequestId(focusId);
  }, [location.search]);

  useEffect(() => {
    if (focusedRequestId && openSection !== 'requests') {
      console.log('[MatchesAndRequestsPage] Focusing on requests section');
      setOpenSection('requests');
    }
  }, [focusedRequestId, openSection]);

  function handleUnfocus() {
    const newParams = new URLSearchParams(location.search);
    newParams.delete('focusRequestId');
    navigate(
      { pathname: location.pathname, search: newParams.toString() },
      { replace: true }
    );
  }

  // --------------------------------------
  // Helpers for modal and UI
  // --------------------------------------
  async function openProfileModal(user, eventName = "") {
    console.log("[MatchesAndRequestsPage] Opening profile modal for user:", user?._id || user?.id || "(no id)");
    // Open immediately with whatever we have so the UI is responsive
    setProfileModalUser(user);
    setProfileModalEvent(eventName);
    setIsProfileModalOpen(true);
    // Hydrate with full profile if we only have a partial user object
    try {
      const userId = String(user?._id || user?.id || "");
      if (!userId) return; // nothing to fetch
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/users/${encodeURIComponent(userId)}`, {
        ...authHeaders(),
      });
      if (data && typeof data === "object") {
        // Merge to preserve any local fields and update with server-side profile fields
        setProfileModalUser((prev) => ({ ...(prev || {}), ...data }));
      }
    } catch (e) {
      console.warn("[Friends.js] Failed to hydrate profile for modal:", e?.response?.data || e?.message);
      // keep showing the lightweight info; nothing else to do
    }
  }

  function closeProfileModal() {
    setProfileModalUser(null);
    setProfileModalEvent('');
    setIsProfileModalOpen(false);
  }

  function getProfilePicUrl(userObj) {
    return userObj?.profilePicUrl || "/default-avatar.png";
  }
  
function isHostUser(user) {
  const userId = String(user?._id || '');

  if (userId && hostUserIds.has(userId)) {
    return true;
  }

  // Fast path in case email already came with the user object.
  const email = String(user?.email || '')
    .trim()
    .toLowerCase();

  return !!email && hostEmails.has(email);
}

  function calculateAge(u) {
    if (!u?.dateOfBirth) return '';
    const dob = new Date(u.dateOfBirth);
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  }

  // --------------------------------------
  // RENDER REQUESTS
  // --------------------------------------
  function renderRequests() {
    if (requestsCount === 0) {
      console.log('[MatchesAndRequestsPage] No friend requests to render.');
      return <p style={styles.emptyText}>No friend requests at the moment.</p>;
    }
    return (
      <div style={styles.requestsWrapper}>
{validNeedDecide.map(({ req, view }) => {
              console.log('[MatchesAndRequestsPage] Rendering request:', req._id);
          const { _id, fromUserId, toUserId } = req;
          const iAmReceiver =
            String(toUserId?._id || toUserId) === String(currentUserId);
          const otherUser = iAmReceiver ? fromUserId : toUserId;
          const requestPicUrl = getProfilePicUrl(otherUser);
          const age = calculateAge(otherUser);
          const sex = otherUser?.gender || '';
          const titleText = `${otherUser.firstName}${
            otherUser.lastName ? ' ' + otherUser.lastName.charAt(0).toUpperCase() + '.' : ''
          }`;
          const subtitle = age ? `${age} • ${sex}` : sex;

          const isHost = isHostUser(otherUser);

          let badge = null;
          if (view.category === 'DECLINED_BY_ME') {
            badge = 'DECLINED';
          }

          // 3) Wrap "onAccept" call to show toaster
          function handleAcceptClick() {
            onAccept(_id);
            addToast(
              `You and ${titleText} are now connected! Find them in your contacts to start chatting.`,
              'success'
            );
          }

          let actions;
          if (view.category === 'DECLINED_BY_ME') {
            actions = (
              <button style={styles.btnSecondary} onClick={() => onUndo(_id)}>
                <IconFA name="undo" style={{ marginRight: 4 }} />
                Undo
              </button>
            );
          } else if (iAmReceiver) {
            actions = (
              <>
                <button style={styles.btnPrimary} onClick={handleAcceptClick}>
                  <IconFA name="check" />
                  Accept
                </button>
                <button
                  style={{ ...styles.btnDanger }}
                  onClick={() => onDecline(_id)}
                >
                  <IconFA name="times" />
                  Decline
                </button>
              </>
            );
          } else {
            actions = (
              <button style={styles.btnSecondary} onClick={() => onUndo(_id)}>
                <IconFA name="undo" style={{ marginRight: 4 }} />
                Undo
              </button>
            );
          }

          return (
            <div
              key={_id}
              style={{
                ...styles.requestItem,
                ...(String(_id) === focusedRequestId ? styles.focusedStyle : {}),
              }}
            >
              <img
                src={requestPicUrl}
                alt="avatar"
                style={styles.requestAvatar}
                onClick={() => openProfileModal(otherUser)}
              />
              <div style={styles.requestInfo}>
<p style={styles.requestTitle}>{titleText.trim()}</p>

{subtitle && <p style={styles.matchSub}>{subtitle}</p>}

{isHost && (
  <span style={styles.hostPill}>
    HOST
  </span>
)}
                {badge && <span style={styles.requestBadge}>{badge}</span>}
              </div>
              <div style={styles.requestActions}>{actions}</div>
            </div>
          );
        })}
      </div>
    );
  }

  // --------------------------------------
  // RENDER MATCHES (already pairwise)
  // --------------------------------------
  function renderMatches() {
    if (matchesCount === 0) {
      console.log('[MatchesAndRequestsPage] No matches to render.');
return <p style={styles.emptyText}>You don’t have any connections right now.</p>;
    }
    return pairsToShow.map(({ match, req, view, otherUser }) => {
      console.log('[MatchesAndRequestsPage] Rendering match row for match:', match._id, 'otherUser:', otherUser._id);
      const age = calculateAge(otherUser);
      const sex = otherUser?.gender || '';
      const title = `${otherUser.firstName}${
        otherUser.lastName ? ' ' + otherUser.lastName.charAt(0).toUpperCase() + '.' : ''
      }`;
      const subtitle = age ? `${age} • ${sex}` : sex;

const isHost = isHostUser(otherUser);

      const isRequestSent = req && view?.category === 'SENT';
      let actions;
      if (isRequestSent) {
        actions = (
          <button style={styles.btnSecondary} onClick={() => onCancel(req._id)}>
            Cancel
          </button>
        );
      } else {
        actions = (
          <button
            style={styles.btnPrimary}
            onClick={() => onSendRequest(match._id, otherUser._id)}
          >
            Send Request
          </button>
        );
      }
      return (
        <div key={`${match._id}_${otherUser._id}`} style={styles.matchItem}>
          <img
            src={getProfilePicUrl(otherUser)}
            alt="avatar"
            style={styles.matchAvatar}
            onClick={() => openProfileModal(otherUser)}
          />
          <div style={styles.matchInfo}>
<p style={styles.matchName}>{title.trim()}</p>

{subtitle && <p style={styles.matchSub}>{subtitle}</p>}

{isHost && (
  <span style={styles.hostPill}>
    HOST
  </span>
)}
          </div>
          <div style={styles.matchActions}>{actions}</div>
        </div>
      );
    });
  }

  // Accordion sections
  // Replace your current `sections` with this:
const sections = [
  {
    key: 'requests',
    title: 'Friend Requests',
    count: requestsCount,
    icon: (
      <div style={accordionStyles.headerIconWrap}>
        <LuUserPlus style={accordionStyles.headerIcon} />
      </div>
    ),
    content: renderRequests(),
  },
  {
    key: 'matches',
title: 'People You Met',
    count: matchesCount,
    icon: (
      <div style={accordionStyles.headerIconWrap}>
        <LuHandshake style={accordionStyles.headerIcon} />
      </div>
    ),
    content: renderMatches(),
  },
];


  function handleToggle(sectionKey) {
    console.log('[MatchesAndRequestsPage] Toggling section:', sectionKey);
    setOpenSection(openSection === sectionKey ? null : sectionKey);
  }

  return (
    <div style={pageShell}>
      <div style={contentFlex}>
        <div style={styles.pageWrapper}>
          {focusedRequestId && (
            <div
              onClick={handleUnfocus}
              style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 50 }}
            />
          )}
  
          {sections.map((sec) => (
            <AccordionSection
              key={sec.key}
              sectionKey={sec.key}
              title={sec.title}
              count={sec.count}
              icon={sec.icon}
              isOpen={openSection === sec.key}
              onToggle={() => handleToggle(sec.key)}
            >
              {sec.content}
            </AccordionSection>
          ))}
  
          {isProfileModalOpen && profileModalUser && (
            <ProfileActionSheet
              open={isProfileModalOpen}
              onClose={closeProfileModal}
              user={profileModalUser}
              onUserBlocked={onUserBlocked}
                isHost={isHostUser(profileModalUser)}

            />
          )}
        </div>
      </div>
  
      {/* Footer sits at the bottom just like ProfilePage */}
      <MinimalFooter />
    </div>
  );  
}

/** Accordion section component */
function AccordionSection({ sectionKey, title, count, icon, isOpen, onToggle, children }) {
  return (
    <div style={accordionStyles.sectionContainer}>
      <div style={accordionStyles.sectionHeader} onClick={onToggle}>
        <div
          style={{
            ...accordionStyles.headerBg,
            ...(isOpen ? accordionStyles.headerBgOpen : {}),
          }}
        />
        {icon}
        <div style={accordionStyles.headerText}>{title}</div>
        {count > 0 && <div style={accordionStyles.countBadge}>{count}</div>}
        <div
          style={{
            ...accordionStyles.chevronWrapper,
            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        >
          <IoChevronForward style={accordionStyles.chevronIcon} />
        </div>
      </div>
      {isOpen && (
        <div style={accordionStyles.expandedContent}>
          {children}
        </div>
      )}
      <div style={accordionStyles.divider} />
    </div>
  );
}
// -------------------------------
// Accordion styles (updated)
// -------------------------------
const accordionStyles = {
  sectionContainer: {
    marginBottom: 20,
    width: "100%",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: "0px 0",
    gap: 10,
  },

  // NEW: grey circular icon wrapper (like Contacts/Notifications)
  headerIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    background: "#F7F8FA",
    border: "0px solid #E7E9EE",
    flexShrink: 0,
  },
  headerIcon: {
    fontSize: 18,
    color: "#000",
    lineHeight: 1,
  },

  headerBg: { display: "none" },
  headerBgOpen: {},

  headerText: {
    fontSize: 14,
    fontWeight: 700,
    color: '#000'
  },

  // UPDATED: counter pill is BLUE (not black)
  countBadge: {
    marginLeft: 8,
    backgroundColor: "#3B6BFF",
    border: "1px solid #3B6BFF",
    color: "#fff",
    fontSize: "0.75rem",
    padding: "2px 8px",
    borderRadius: 999,
    fontWeight: 800,
    lineHeight: 1,
  },

  chevronWrapper: {
    marginLeft: "auto",
    transition: "transform 0.2s ease",
    display: "flex",
    alignItems: "center",
  },
  chevronIcon: {
    fontSize: "1.2rem",
    color: "#9CA3AF",
  },

  expandedContent: {
    marginTop: 12,
    padding: "4px 0",
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "#fff",
  },
  divider: {
    marginTop: 20,
    width: "100%",
    height: 1,
    backgroundColor: "#E5E7EB",
  },
};

// -------------------------------
// Main page styles (unchanged except fallback icon size)
// -------------------------------
const styles = {
  pageWrapper: {
    width: "100%",
    maxWidth: "900px",
    margin: "20px auto",
    fontFamily:
      "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    padding: "0 16px",
    boxSizing: "border-box",
    color: "#111",
  },

  // Fallback if you use a bare icon (prefer headerIconWrap/headerIcon above)
  accordionIcon: {
    fontSize: 22,
    color: "#6B7280",
    lineHeight: 1,
  },

  emptyText: {
    fontSize: "0.9rem",
    color: "#9CA3AF",
    margin: "8px 0 12px",
  },

  requestsWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  /* ===== CARDS (match chat/contacts/request pages) ===== */
  requestItem: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #E7E9EE",     // <-- hairline border
    borderRadius: 12,
    padding: 12,                      // <-- same spacing
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "#fff",
    transition: "transform 0.15s ease",
    marginBottom: 8,
  },
  requestAvatar: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    objectFit: "cover",
    marginRight: 12,
    cursor: "pointer",
    background: "#F3F4F6",            // <-- pill bg
    border: "2px solid #E7E9EE",      // <-- hairline ring
  },
  requestInfo: {
    flex: 1,
    minWidth: 0,
  },
  requestTitle: {
    margin: 0,
    fontSize: 14,                     // <-- unified scale
    fontWeight: 800,
    letterSpacing: "-0.01em",
    color: "#0B1020",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  matchSub: {
    margin: "2px 0 0 0",
    fontSize: 12,
    color: "#6B7280",
  },
  requestBadge: {
    display: "inline-block",
    backgroundColor: "#FEE2E2",
    color: "#B91C1C",
    borderRadius: 999,
    fontSize: 12,
    padding: "2px 8px",
    marginTop: 4,
    fontWeight: 700,
    lineHeight: 1,
  },
  requestActions: {
    display: "flex",
    alignItems: "center",
    marginLeft: 12,
    flexWrap: "wrap",
    gap: 8,
  },

  matchItem: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #E7E9EE",     // <-- hairline border
    borderRadius: 12,
    padding: 12,                      // <-- same spacing
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "#fff",
    transition: "transform 0.15s ease",
    marginBottom: 8,
  },
  matchAvatar: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    objectFit: "cover",
    marginRight: 12,
    cursor: "pointer",
    background: "#F3F4F6",            // <-- pill bg
    border: "2px solid #E7E9EE",      // <-- hairline ring
  },
  matchInfo: {
    flex: 1,
    minWidth: 0,
  },
  matchName: {
    margin: 0,
    fontSize: 14,                     // <-- unified scale
    fontWeight: 800,
    letterSpacing: "-0.01em",
    color: "#0B1020",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  matchNameRow: {
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0,
},

hostPill: {
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
  marginTop: 4,
  padding: "2px 6px",
  borderRadius: 999,
  backgroundColor: "#F3F4F6",
  color: "#6B7280",
  fontSize: 9,
  fontWeight: 800,
  letterSpacing: "0.06em",
  lineHeight: 1.2,
  marginLeft:-5,
},
  matchActions: {
    marginLeft: 12,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },

  /* Buttons with perfectly centered text (icon-friendly) */
btnPrimary: {
  backgroundColor: "#198754", // green
  color: "#fff",
  fontSize: 12,
  border: "none",
  borderRadius: 10,
  padding: "8px 14px 8px 14px",
  cursor: "pointer",
  fontWeight: 600,
  textAlign: "center",
  lineHeight: 1,
  alignItems: "center",
  justifyItems: "center",
},

btnSecondary: {
  backgroundColor: "#F3F4F6",
  color: "#111",
  fontSize: 12,
  border: "none",
  borderRadius: 10,
  padding: "8px 14px 8px 14px",
  cursor: "pointer",
  fontWeight: 600,
  textAlign: "center",
  lineHeight: 1,
  // same centering trick
  alignItems: "center",
  justifyItems: "center",
},

btnDanger: {
  backgroundColor: "#DC2626",
  color: "#fff",
  fontSize: 12,
  border: "none",
  borderRadius: 10,
  padding: "8px 14px 8px 14px",
  cursor: "pointer",
  fontWeight: 600,
  textAlign: "center",
  lineHeight: 1,
  alignItems: "center",
  justifyItems: "center",
},


  focusedStyle: {
    transform: "scale(1.02)",
    boxShadow: "0 0 12px rgba(66,133,244,0.25)", // matches focused look used elsewhere
    border: "1px solid #4285F4",
  },
};
const pageShell = {
  display: "flex",
  flexDirection: "column",
  minHeight: "80vh",
  background: "#FFFFFF",
};

const contentFlex = {
  flex: 1, // lets content grow and pushes footer to the bottom
};